import React, { createContext, useCallback, useContext, useEffect, useState } from 'react'
import { saveToken, loadToken, clearToken, getAuthHeader, AUTH_UNAUTHORIZED_EVENT } from './token'

// Re-export so existing importers of `getAuthHeader` from this module keep working.
export { getAuthHeader }

// ─── Types ────────────────────────────────────────────────────────────────────
export type Role = 'buyer' | 'seller' | 'operator' | 'admin'

export interface AuthUser {
  id:           string
  email:        string
  username:     string
  role:         Role
}

interface AuthState {
  user:    AuthUser | null
  loading: boolean
  error:   string | null
}

interface AuthApi extends AuthState {
  login:  (email: string, password: string) => Promise<void>
  logout: () => void
}

const AuthContext = createContext<AuthApi | null>(null)

////// Offline mode //////
// VITE_OFFLINE_MODE=true lets frontend devs log in without a running backend.
// Logins are matched against the dummy users below; no requests are made.
const OFFLINE_MODE = import.meta.env.VITE_OFFLINE_MODE === 'true'
const OFFLINE_USER_KEY = 'firex_offline_user'

// Keyed by email (the login form's 'username' field). Edit roles/creds freely.
const OFFLINE_USERS: Record<string, { password: string; user: AuthUser }> = {
  'buyer@firex.dev': {
    password: 'buyer',
    user: { id: 'offline-buyer', email: 'buyer@firex.dev', username: 'Offline Buyer', role: 'buyer' },
  },
  'seller@firex.dev': {
    password: 'seller',
    user: { id: 'offline-seller', email: 'seller@firex.dev', username: 'Offline Seller', role: 'seller' },
  },
}

// ─── Provider ─────────────────────────────────────────────────────────────────
export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>({
   /* 
  id:       'deadbeef-0000-0000-0000-0000000000b1',  // matches backend hardcoded UUID
  email:    'buyer@firex.io',
  username: 'buyer',
  role:     'buyer',
*/
  id:       'deadbeef-0000-0000-0000-0000000000b1',
  email:    'seller@firex.io',
  username: 'SEC Seller',
  role:     'seller',   // ← change from 'buyer' to 'seller'*/
})
  const [loading, setLoading] = useState(true)
  const [error, setError]     = useState<string | null>(null)

  // On mount — restore the session.
  useEffect(() => {
    // Offline: rehydrate the dummy user from localStorage, no network call.
    if (OFFLINE_MODE) {
      const raw = localStorage.getItem(OFFLINE_USER_KEY)
      if (raw) setUser(JSON.parse(raw))
      setLoading(false)
      return
    }

    // Online: if a token is stored, validate it against /api/users/me.
    const token = loadToken()
    if (!token) { setLoading(false); return }

    fetch('/api/users/me', {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(async r => {
        if (r.status === 401) { clearToken(); setUser(null); return }
        const body = await r.json()
        setUser(body)
      })
      .catch(() => { clearToken(); setUser(null) })
      .finally(() => setLoading(false))
  }, [])

  const login = useCallback(async (username: string, password: string) => {
    setError(null)

    // Offline: match against the dummy users, persist, no network call.
    if (OFFLINE_MODE) {
      const entry = OFFLINE_USERS[username]
      if (!entry || entry.password !== password) {
        const msg = 'Login failed'
        setError(msg)
        throw new Error(msg)
      }
      localStorage.setItem(OFFLINE_USER_KEY, JSON.stringify(entry.user))
      setUser(entry.user)
      return
    }

    // OAuth2 password flow requires form-encoded body
    const form = new URLSearchParams()
    form.append('username', username)   // backend uses 'username' field for email
    form.append('password', password)

    const r = await fetch('/api/token', {
      method:  'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body:    form.toString(),
    })

    if (!r.ok) {
      const msg = 'Login failed'
      setError(msg)
      throw new Error(msg)
    }

    const { access_token } = await r.json()
    saveToken(access_token)

    // Fetch user profile with the new token
    const meRes = await fetch('/api/users/me', {
      headers: { Authorization: `Bearer ${access_token}` },
    })
    const me = await meRes.json()
    setUser(me)
  }, [])

  const logout = useCallback(() => {
    clearToken()
    // Offline: Remove user key
    localStorage.removeItem(OFFLINE_USER_KEY)
    setUser(null)
  }, [])

  // End the session when any API call reports a 401 (e.g. the token expired).
  // App renders <Login /> once user is null, so logout() doubles as the redirect.
  useEffect(() => {
    window.addEventListener(AUTH_UNAUTHORIZED_EVENT, logout)
    return () => window.removeEventListener(AUTH_UNAUTHORIZED_EVENT, logout)
  }, [logout])

  return (
    <AuthContext.Provider value={{ user, loading, error, login, logout }}>
      {children}
    </AuthContext.Provider>
  )
}

// ─── Hook ─────────────────────────────────────────────────────────────────────
export function useAuth(): AuthApi {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>')
  return ctx
}